import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import api, { fileUrl } from "@/lib/api";
import { SavePlaceDialog } from "@/components/SavePlaceDialog";
import {
  Plus, Trash2, Pencil, ExternalLink, Bookmark, Building2, Lightbulb, MapPin,
  Search, Utensils, Coffee, X, Tag, Map, Plane,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

const locationLabel = (item) => [item.city, item.country].filter(Boolean).join(", ") || item.address || "";

const CATS = {
  place: { label: "Place", icon: MapPin, c: "text-emerald-700 bg-emerald-100" },
  hotel: { label: "Hotel", icon: Building2, c: "text-sky-700 bg-sky-100" },
  restaurant: { label: "Restaurant", icon: Utensils, c: "text-rose-700 bg-rose-100" },
  cafe: { label: "Café", icon: Coffee, c: "text-amber-700 bg-amber-100" },
  idea: { label: "Idea", icon: Lightbulb, c: "text-violet-700 bg-violet-100" },
};
const catOf = (c) => CATS[c] || CATS.place;

export const MySaves = ({ refreshKey }) => {
  const [items, setItems] = useState([]);
  const [trips, setTrips] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState("");
  const createFormRef = useRef(null);
  const editFormRef = useRef(null);
  const closeForm = (formRef, close) => formRef.current ? formRef.current.requestClose(close) : close();

  const load = async () => setItems((await api.get("/saves")).data);
  const loadTrips = async () => setTrips((await api.get("/trips")).data);
  useEffect(() => { load(); loadTrips(); }, [refreshKey]);

  const remove = async (id) => {
    await api.delete(`/saves/${id}`);
    setDetail(null);
    load();
  };

  const tripName = (id) => trips.find((t) => t.id === id)?.title;

  const q = query.trim().toLowerCase();
  const shown = !q ? items : items.filter((i) => {
    const hay = [i.title, i.city, i.country, i.address, i.notes, catOf(i.category).label, ...(i.keywords || [])]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  return (
    <div data-testid="my-saves-panel" className="space-y-4">
      <div data-testid="saves-header" className="mobile-section-header relative flex items-start flex-wrap gap-3 w-full px-5 py-4 rounded-3xl bg-white/95 backdrop-blur-md border border-white/60 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
        <div>
          <h2 data-testid="saves-heading" className="text-2xl sm:text-3xl font-bold font-['Nunito'] text-stone-900 tracking-tight">My Saves</h2>
          <p data-testid="saves-tagline" className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400 mt-1">Restaurants, hotels, places</p>
        </div>
        <Dialog open={open} onOpenChange={(value) => { if (value) setOpen(true); else closeForm(createFormRef, () => setOpen(false)); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-save-btn" size="icon" aria-label="Save a place" title="Save a place" className="absolute right-5 top-4 rounded-full bg-ac hover:bg-ac-hover text-white shrink-0 hover:-translate-y-0.5 transition-transform">
              <Plus className="w-5 h-5" strokeWidth={3} aria-hidden="true" />
            </Button>
          </DialogTrigger>
          {open && <SavePlaceDialog ref={createFormRef} onSaved={() => { setOpen(false); load(); }} />}
        </Dialog>
      </div>

      {/* Keyword search */}
      <div data-testid="saves-search-panel" className="relative w-full rounded-3xl bg-white/95 backdrop-blur-md border border-white/60 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
        <Search aria-hidden="true" className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
        <Input
          data-testid="saves-search-input"
          aria-label="Search saved places by name, keyword, or area"
          placeholder="Search by name, keyword, area"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="rounded-3xl pl-12 pr-10 h-14 bg-transparent border-0 shadow-none text-sm"
        />
        {query && (
          <button type="button" onClick={() => setQuery("")} data-testid="saves-search-clear" aria-label="Clear search" title="Clear search" className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors">
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {shown.length === 0 ? (
        <div data-testid="saves-empty-state" role="status" className="w-full rounded-3xl border border-white/60 bg-white/95 backdrop-blur-md shadow-[0_8px_24px_rgba(0,0,0,0.14)] py-16 flex flex-col items-center text-center px-6">
          <span className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 grid place-items-center mb-4"><Bookmark className="w-7 h-7" strokeWidth={2.5} /></span>
          <h3 data-testid="saves-empty-title" className="text-xl font-bold font-['Nunito'] text-stone-900">{q ? "No matches" : "Nothing saved yet"}</h3>
          <p data-testid="saves-empty-message" className="text-stone-500 mt-1 max-w-xs">{q ? "Try a different keyword." : "Search a hotel or restaurant, tag it with your own keywords, and find it later."}</p>
        </div>
      ) : (
        <div data-testid="saves-cards" className="flex flex-col gap-4 w-full">
          {shown.map((item, i) => {
            const cat = catOf(item.category);
            const Icon = cat.icon;
            return (
              <motion.button
                key={item.id}
                data-testid={`save-card-${item.id}`}
                onClick={() => setDetail(item)}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="w-full text-left rounded-2xl border border-white/60 bg-white/95 backdrop-blur-md shadow-[0_8px_24px_rgba(0,0,0,0.14)] hover:-translate-y-0.5 hover:bg-white hover:shadow-lg transition-[transform,background-color,box-shadow] duration-300 px-5 py-4 flex items-start gap-3"
              >
                <span className={`w-10 h-10 rounded-xl grid place-items-center flex-shrink-0 ${cat.c}`}>
                  <Icon className="w-5 h-5" strokeWidth={2.5} />
                </span>
                <span className="min-w-0 flex-1">
                  <span data-testid={`save-card-title-${item.id}`} className="block font-bold font-['Nunito'] text-stone-900 leading-tight truncate">{item.title}</span>
                  {locationLabel(item) && <span data-testid={`save-card-address-${item.id}`} className="block text-sm text-stone-500 truncate">{locationLabel(item)}</span>}
                  {!!item.keywords?.length && <span data-testid={`save-card-keywords-${item.id}`} className="flex flex-wrap gap-1.5 mt-2">
                    {item.keywords.map((keyword, index) => <span key={`${keyword}-${index}`} data-testid={`save-card-keyword-${item.id}-${index}`} className="inline-flex max-w-full items-start gap-1 bg-ac-soft text-ac-softfg text-xs font-bold px-2.5 py-1 rounded-full">
                      <Tag className="w-3 h-3 shrink-0" aria-hidden="true" /> <span className="min-w-0 break-words">{keyword}</span>
                    </span>)}
                  </span>}
                  {item.trip_id && tripName(item.trip_id) && (
                    <span data-testid={`save-trip-badge-${item.id}`} className="mt-1 inline-flex max-w-full items-center gap-1 bg-ac-soft text-ac-softfg text-[11px] font-bold px-2 py-0.5 rounded-full">
                      <Plane className="w-3 h-3 shrink-0" /> <span className="min-w-0 truncate">{tripName(item.trip_id)}</span>
                    </span>
                  )}
                </span>
              </motion.button>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(value) => {
        if (!value) {
          const close = () => { setDetail(null); setEditing(false); };
          if (editing) closeForm(editFormRef, close); else close();
        }
      }}>
        {detail && (editing
          ? <SavePlaceDialog ref={editFormRef} key={detail.id} initialItem={detail} onCancel={() => setEditing(false)} onSaved={(updated) => {
              setItems((current) => current.map((item) => item.id === updated.id ? updated : item));
              setDetail(null);
              setEditing(false);
            }} />
          : <DetailDialog item={detail} onEdit={() => setEditing(true)} onDelete={() => remove(detail.id)} />)}
      </Dialog>
    </div>
  );
};

/* ---------------- Detail dialog ---------------- */
const DetailDialog = ({ item, onDelete, onEdit }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const deletePlace = async () => {
    if (deleting) return;
    setDeleting(true); setDeleteError("");
    try { await onDelete(); }
    catch { setDeleteError("Could not delete this place. Please try again."); }
    finally { setDeleting(false); }
  };

  return (
    <DialogContent data-testid="save-detail-dialog" aria-describedby={undefined} className="rounded-3xl font-['Figtree'] max-h-[88vh] overflow-y-auto py-4 gap-3">
      <DialogHeader className="pr-5">
        <DialogTitle data-testid="save-detail-title" className="font-['Nunito'] text-2xl text-left break-words">{item.title}</DialogTitle>
      </DialogHeader>

      <div className="space-y-2.5">
        {locationLabel(item) && (
          <div className="flex items-start gap-2 text-stone-600">
            <MapPin className="w-4 h-4 mt-0.5 text-stone-400 flex-shrink-0" />
            <span data-testid="save-detail-location" className="text-sm min-w-0 break-words">{locationLabel(item)}</span>
          </div>
        )}
        {item.keywords?.length > 0 && (
          <div data-testid="save-detail-keywords" className="flex flex-wrap gap-1.5">
            {item.keywords.map((k) => (
              <span key={k} data-testid={`detail-keyword-${k}`} className="flex max-w-full items-start gap-1 bg-ac-soft text-ac-softfg text-xs font-bold px-2.5 py-1 rounded-full">
                <Tag className="w-3 h-3 shrink-0" /> <span className="min-w-0 break-words">{k}</span>
              </span>
            ))}
          </div>
        )}

        {item.image_path && <img data-testid="save-detail-cover" src={fileUrl(item.image_path)} alt={item.title} className="w-full h-44 object-cover rounded-2xl" />}
        {item.notes && (
          <p data-testid="save-detail-notes" className="text-stone-600 text-sm leading-relaxed break-words bg-stone-50 rounded-2xl p-3">{item.notes}</p>
        )}

        {(item.map_url) && (
          <a href={item.map_url} target="_blank" rel="noreferrer" data-testid="detail-map-link" className="inline-flex items-center gap-2 text-white bg-ac hover:bg-ac-hover font-bold rounded-full px-4 py-2 text-sm transition-colors">
            <Map className="w-4 h-4" /> Open in Maps <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        {item.source === "osm" && <p data-testid="save-detail-attribution" className="text-xs text-stone-400">© OpenStreetMap contributors</p>}
      </div>

      <DialogFooter className="flex-row gap-2 sm:space-x-0">
        <Button data-testid="detail-edit-btn" onClick={onEdit} disabled={deleting} variant="outline" className="rounded-full font-bold text-stone-700 flex-1">
          <Pencil className="w-4 h-4 mr-2" /> Edit
        </Button>
        <Button data-testid="detail-delete-btn" onClick={() => { setDeleteError(""); setConfirmDelete(true); }} disabled={deleting} variant="outline" className="rounded-full font-bold text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 flex-1">
          <Trash2 className="w-4 h-4 mr-2" /> Delete
        </Button>
      </DialogFooter>
      <AlertDialog open={confirmDelete} onOpenChange={(value) => { if (!deleting) setConfirmDelete(value); }}>
        <AlertDialogContent data-testid="save-delete-confirm-dialog" className="w-[calc(100%-2rem)] max-w-sm rounded-3xl font-['Figtree']" onEscapeKeyDown={(event) => { event.stopPropagation(); if (deleting) event.preventDefault(); }} onCloseAutoFocus={(event) => { event.preventDefault(); document.querySelector('[data-testid="detail-delete-btn"]')?.focus(); }}>
          <AlertDialogHeader className="text-left">
            <AlertDialogTitle data-testid="save-delete-title" className="font-['Nunito']">Delete this place?</AlertDialogTitle>
            <AlertDialogDescription data-testid="save-delete-description">This place will be permanently deleted. This can’t be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && <p data-testid="save-delete-error" role="alert" className="text-sm text-rose-600">{deleteError}</p>}
          <AlertDialogFooter className="flex-row gap-2 sm:space-x-0">
            <AlertDialogCancel data-testid="save-delete-cancel" disabled={deleting} className="mt-0 flex-1 rounded-full font-bold">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="save-delete-confirm" disabled={deleting} onClick={(event) => { event.preventDefault(); deletePlace(); }} className="flex-1 rounded-full bg-rose-600 hover:bg-rose-700 text-white font-bold">{deleting ? "Deleting…" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogContent>
  );
};
